/**
 * Utilities for OAuth 2.0 Authorization Code Flow with PKCE
 */

/**
 * Generates a cryptographically random code verifier
 * @returns {string} Base64url-encoded string
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Generates a code challenge from a code verifier using SHA256
 * @param {string} codeVerifier - The code verifier
 * @returns {Promise<string>} Base64url-encoded SHA256 hash of the code verifier
 */
export async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
}

/**
 * Base64url-encode the input Uint8Array
 * @param {Uint8Array} buffer - Input buffer
 * @returns {string} Base64url-encoded string
 */
function base64URLEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Builds the Google OAuth authorization URL with PKCE parameters
 * @param {string} clientId - Google Client ID
 * @param {string} redirectUri - Redirect URI
 * @param {string} codeChallenge - Code challenge
 * @param {string} state - Optional state parameter
 * @returns {string} Authorization URL
 */
export function buildGoogleAuthUrl(clientId, redirectUri, codeChallenge, state = '') {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'profile email',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...(state && { state }),
  });

  const finalUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

  return finalUrl;
}

/**
 * Builds the Microsoft OAuth authorization URL with PKCE parameters
 * @param {string} clientId - Microsoft Client ID
 * @param {string} redirectUri - Redirect URI
 * @param {string} codeChallenge - Code challenge
 * @param {string} state - State parameter for security
 * @returns {string} Authorization URL
 */
export function buildMicrosoftAuthUrl(clientId, redirectUri, codeChallenge, state) {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'openid profile email User.Read',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    prompt: 'select_account',
    state: state,
  });

  const finalUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;

  return finalUrl;
}

/**
 * Stores Google PKCE parameters in both sessionStorage and localStorage.
 * localStorage serves as a backup in case sessionStorage is cleared.
 */
export function storePKCEData(codeVerifier, state = '') {
  sessionStorage.setItem('google_oauth_code_verifier', codeVerifier);
  localStorage.setItem('google_oauth_code_verifier', codeVerifier);
  if (state) {
    sessionStorage.setItem('google_oauth_state', state);
    localStorage.setItem('google_oauth_state', state);
  }
}

/**
 * Stores Microsoft PKCE parameters in both sessionStorage and localStorage.
 * localStorage serves as a backup in case sessionStorage is cleared
 * (e.g., by cleanupLoginData during token refresh failures).
 */
export function storeMicrosoftPKCEData(codeVerifier, state) {
  sessionStorage.setItem('microsoft_oauth_code_verifier', codeVerifier);
  sessionStorage.setItem('microsoft_oauth_state', state);
  localStorage.setItem('microsoft_oauth_code_verifier', codeVerifier);
  localStorage.setItem('microsoft_oauth_state', state);
}

/**
 * Retrieves and clears Google PKCE data.
 * Tries sessionStorage first, falls back to localStorage backup.
 */
export function retrieveAndClearPKCEData() {
  let codeVerifier = sessionStorage.getItem('google_oauth_code_verifier');
  let state = sessionStorage.getItem('google_oauth_state');

  if (!codeVerifier) {
    codeVerifier = localStorage.getItem('google_oauth_code_verifier');
  }
  if (!state) {
    state = localStorage.getItem('google_oauth_state');
  }

  sessionStorage.removeItem('google_oauth_code_verifier');
  sessionStorage.removeItem('google_oauth_state');
  localStorage.removeItem('google_oauth_code_verifier');
  localStorage.removeItem('google_oauth_state');

  return { codeVerifier, state };
}

/**
 * Retrieves and clears Microsoft PKCE data.
 * Tries sessionStorage first, falls back to localStorage backup.
 */
export function retrieveAndClearMicrosoftPKCEData() {
  let codeVerifier = sessionStorage.getItem('microsoft_oauth_code_verifier');
  let state = sessionStorage.getItem('microsoft_oauth_state');

  if (!codeVerifier || !state) {
    codeVerifier = codeVerifier || localStorage.getItem('microsoft_oauth_code_verifier');
    state = state || localStorage.getItem('microsoft_oauth_state');
  }

  sessionStorage.removeItem('microsoft_oauth_code_verifier');
  sessionStorage.removeItem('microsoft_oauth_state');
  localStorage.removeItem('microsoft_oauth_code_verifier');
  localStorage.removeItem('microsoft_oauth_state');

  return { codeVerifier, state };
}
